import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Scale } from "lucide-react";
import { LEGAL_DOCS } from "@/legal/config";

const SKIP_PATHS = ["/select-role", "/auth/callback", "/payment/", "/terms", "/privacy", "/seller-agreement", "/stage-terms", "/refund-policy", "/community-guidelines", "/ip-policy", "/cookie-policy", "/legal"];

export default function LegalAcceptModal() {
  const { user } = useAuth();
  const loc = useLocation();
  const [status, setStatus] = useState(null);
  const [ticked, setTicked] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user || user === false || !user.disclaimer_ack) { setStatus(null); return; }
    const t = setTimeout(() => api.get("/legal/status").then(({ data }) => setStatus(data)).catch(() => setStatus(null)), 1500);
    return () => clearTimeout(t);
  }, [user]);

  const show = status && status.missing?.length > 0 && !SKIP_PATHS.some((p) => loc.pathname.startsWith(p));
  if (!show) return null;

  const docs = LEGAL_DOCS.filter((d) => status.missing.includes(d.slug));
  const accept = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/legal/accept", { docs: status.missing, context: "version-gate" });
      setStatus(data);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-6" data-testid="legal-accept-modal">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      <div className="relative w-full max-w-lg rounded-xl border border-white/10 bg-[#121212] p-8 shadow-2xl">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#FF9F0A]/40 bg-[#FF9F0A]/10">
          <Scale className="h-3.5 w-3.5 text-[#FF9F0A]" />
          <span className="text-[10px] font-semibold text-[#FF9F0A] uppercase tracking-widest">Updated terms · v{status.version}</span>
        </div>
        <h2 className="mt-4 text-xl font-black tracking-tight text-white">We've updated our legal terms</h2>
        <p className="mt-2 text-sm text-neutral-400">To keep using VISMARKS, please review and accept the current version of:</p>
        <ul className="mt-3 space-y-1.5">
          {docs.map((d) => (
            <li key={d.slug}><Link to={d.path} target="_blank" className="text-sm text-[#007AFF] hover:underline" data-testid={`legal-accept-link-${d.slug}`}>{d.title} ↗</Link></li>
          ))}
        </ul>
        <label className="mt-5 flex items-start gap-3 cursor-pointer rounded-md border border-[#27272A] bg-black/30 p-3.5">
          <input type="checkbox" checked={ticked} onChange={(e) => setTicked(e.target.checked)} data-testid="legal-accept-checkbox" className="mt-0.5 h-4 w-4 accent-[#007AFF]" />
          <span className="text-xs text-neutral-300 leading-relaxed">I have read and agree to the documents above, including the content licence and the fees described in them.</span>
        </label>
        <Button onClick={accept} disabled={!ticked || busy} className="mt-5 w-full h-11 rounded-full bg-white text-black hover:bg-neutral-200 font-bold" data-testid="legal-accept-btn">
          {busy ? "Saving…" : "I agree and continue"}
        </Button>
      </div>
    </div>
  );
}
